import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Selector } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import { AdExchangeCodesEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/adExchange.schema';

import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { useStore as useLineItemStore } from '@/store/local/lineItem';
import { GoalStyled } from './Goal.styled';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';
import type { LineItemStore } from '@/store/local/lineItem';

const AVAILABLE_GOALS = {
  progressGoals: {
    adExchange: {
      grouped: true
    },
    auctionType: {
      grouped: false,
      subGoalKeys: ['openGoals', 'closedGoals']
    },
    creative: {
      grouped: true
    },
    deal: {
      grouped: true
    }
  },
  progressKeys: ['totalProgress', 'dailyProgress'],
  types: [
    {
      enabled: true,
      name: 'Impressions',
      type: 'impressions'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Clicks',
      type: 'clicks'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Video Start',
      type: 'video_start'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Video Complete',
      type: 'video_complete'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Audio Start',
      type: 'audio_start'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Audio Complete',
      type: 'audio_complete'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Pages Viewed',
      type: 'pages_viewed'
    },
    {
      campaignTypes: [CampaignFamilyEnum.standard],
      enabled: true,
      name: 'Engagements',
      type: 'engagements'
    },
    {
      enabled: true,
      name: 'Store Visits',
      type: 'store_visits'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Play Outs',
      type: 'playouts'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male',
      type: 'audience_M'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female',
      type: 'audience_F'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 12-17 Age',
      type: 'audience_M12-17'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 18-24 Age',
      type: 'audience_M18-14'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 25-34 Age',
      type: 'audience_M25-34'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 35-44 Age',
      type: 'audience_M35-44'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 45-54 Age',
      type: 'audience_M45-54'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 55-64 Age',
      type: 'audience_M55-64'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Male 65+ Age',
      type: 'audience_M65+'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 12-17 Age',
      type: 'audience_F12-17'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 18-24 Age',
      type: 'audience_F18-14'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 25-34 Age',
      type: 'audience_F25-34'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 35-44 Age',
      type: 'audience_F35-44'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 45-54 Age',
      type: 'audience_F45-54'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 55-64 Age',
      type: 'audience_F55-64'
    },
    {
      campaignTypes: [CampaignFamilyEnum.dooh],
      enabled: true,
      name: 'Female 65+ Age',
      type: 'audience_F65+'
    }
  ]
};

type AvailableGoal = {
  campaignType?: CampaignFamilyEnum;
  enabled: boolean;
  name: string;
  type: keyof typeof GoalTypeEnum;
};

type State = {
  availableGoals: AvailableGoal[];
  goalType: keyof typeof GoalTypeEnum;
  goalTypeDooh?: string;
  goalValue: number | null;
};

type Props = {
  goal: Pick<LineItemGoal, 'target' | 'type'>;
  limitExceededMessage?: string;
  limits?: Record<string, any>;
  timeFrame: 'daily' | 'overall';
};

export function Goal({ goal, limitExceededMessage, limits, timeFrame }: Props) {
  const { formatMessage } = useIntl();

  const goalTypesDooh = [{ name: 'Media' }, { name: 'Audience' }];
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const state = useLineItemStore() as LineItemStore;
  const { getScreens, setGoal: setFormGoal } = useLineItemFormContext();

  const { setGoal, type: campaignType } = state;

  const lineItemScreens = getScreens();
  const onlyBroadSignScreens = !lineItemScreens?.some(({ adExchange }) => adExchange !== AdExchangeCodesEnum.broadsign);

  const filterAvailableGoals = (goalTypeDooh?: string): AvailableGoal[] => {
    const goalsFilteredByPermissions: AvailableGoal[] = AVAILABLE_GOALS.types.filter(
      // @ts-ignore
      (availableGoal: {
        campaignTypes?: CampaignFamilyEnum[];
        enabled: boolean;
        name: string;
        type: keyof typeof GoalTypeEnum;
      }) => {
        return (
          checkUserResourcePermissions(`campaignGoal${capitalize(availableGoal.type)}`, user.toJSON(), userRoles) &&
          (!availableGoal.campaignTypes || availableGoal.campaignTypes.includes(campaignType!))
        );
      }
    );
    if (isDOOHCampaign(campaignType!.toString())) {
      if (goalTypeDooh === 'Media') {
        return goalsFilteredByPermissions.filter(item => {
          return item.type === 'impressions' || item.type === 'playouts';
        });
      }
      return goalsFilteredByPermissions.filter(item => {
        return isDOOHCampaign(item.campaignType || '') && item.type !== 'playouts';
      });
    }
    return goalsFilteredByPermissions;
  };

  const getGoalTypeDooh = (goalType: string): string | undefined => {
    if (isDOOHCampaign(campaignType!.toString())) {
      const index = goalType === 'impressions' || goalType === 'playouts' ? 0 : 1;
      return goalTypesDooh[index].name;
    }
  };

  const [{ availableGoals, goalType, goalTypeDooh, goalValue }, setState] = useState<State>({
    availableGoals: [],
    goalType: goal.type,
    goalValue: goal.target
  });

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      availableGoals: filterAvailableGoals(getGoalTypeDooh(goal.type)),
      goalTypeDooh: getGoalTypeDooh(goal.type)
    }));
  }, []);

  const setCampaignGoal = (type: keyof typeof GoalTypeEnum, target: number | null) => {
    setFormGoal({
      target,
      timeFrame,
      type
    });
    setGoal({
      target,
      timeFrame,
      type
    });
  };

  const handleGoalValueChange = (newValue: string | FileList | null): void => {
    let target: number | null = newValue ? parseFloat(newValue as string) : null;
    if (target && (isNaN(target) || target < 0)) {
      target = null;
    }
    setState(prevState => ({
      ...prevState,
      goalValue: target
    }));
    setCampaignGoal(goalType, target);
  };

  const handleGoalTypeChange = (newType: keyof typeof GoalTypeEnum): void => {
    setState(prevState => ({
      ...prevState,
      goalType: newType
    }));
    setCampaignGoal(newType, goalValue);
  };

  const handleGoalTypeDoohChange = (newOption: Record<string, any>) => {
    const goalTypeFiltered = filterAvailableGoals(newOption.id);
    setState(prevState => ({
      ...prevState,
      availableGoals: goalTypeFiltered
    }));
    handleGoalTypeChange(goalTypeFiltered[0].type);
  };

  const showAlertMessage = (() => {
    return limitExceededMessage && limits && limits[goalType] != null && goalValue && goalValue > limits[goalType];
  })();

  const activeGoal: AvailableGoal = (() => {
    return availableGoals.find(availableGoal => availableGoal.type === goalType)!;
  })();

  const goalTypeDoohOptions: { id: string; label: string }[] = (() => {
    return goalTypesDooh
      .filter(item => item.name.toLowerCase() === 'media' || onlyBroadSignScreens)
      .map(item => ({
        id: item.name,
        label: item.name
      }));
  })();

  if (!activeGoal) {
    return null;
  }

  return (
    <GoalStyled className="goalsContainer">
      <div className="goalsContainer__inputsWrapper">
        <Input
          autofocus={false}
          className={`${timeFrame}${goal.type.charAt(0).toUpperCase()}${goal.type.slice(1)}__inputWithSymbol`}
          defaultValue={goal && goal.target ? goal.target : undefined}
          id={`${timeFrame}${goal.type.charAt(0).toUpperCase()}${goal.type.slice(1)}CurrencyInput`}
          inputAdditionalProps={{
            min: 0
          }}
          label={formatMessage({
            defaultMessage: timeFrame === 'overall' ? 'Overall goal' : 'Daily goal',
            id: `budgetGoals.goals.${timeFrame}GoalLabel`
          })}
          name="overallBudgetInput"
          notFloatingPlaceholder
          numberFormat="decimal"
          numberOfDecimals={0}
          onChange={handleGoalValueChange}
          placeholder={formatMessage({
            defaultMessage: 'Unlimited',
            id: 'budgetGoals.currencyInputPlaceholder'
          })}
          shape="squareUI"
          type="number"
        />
        {isDOOHCampaign(campaignType!.toString()) && goalTypeDooh && (
          <Selector
            defaultValue={[
              {
                id: goalTypeDooh,
                label: goalTypeDooh
              }
            ]}
            onChange={handleGoalTypeDoohChange}
            options={goalTypeDoohOptions}
            type="simple"
          />
        )}
        <Selector
          defaultValue={[
            {
              id: activeGoal.type,
              label: activeGoal.name
            }
          ]}
          id={`${timeFrame}GoalTypeSelector`}
          // @ts-ignore
          onChange={selectedOption => handleGoalTypeChange(selectedOption.id)}
          options={availableGoals.map(availableGoal => ({
            id: availableGoal.type,
            label: availableGoal.name
          }))}
          type="simple"
          value={[
            {
              id: activeGoal.type,
              label: activeGoal.name
            }
          ]}
        />
      </div>
      {showAlertMessage && <div className="goalsContainer__alertMessage">{limitExceededMessage}</div>}
    </GoalStyled>
  );
}
